<template>
  <div class="card card-charges pb-3">
    <div class="d-flex justify-content-between align-items-start">
      <h2 class="subtitle mb-0">Payments</h2>
      <PaymentStatus
        v-if="payment && !loading"
        :paymentStatus="payment.status"
        :paymentMethod="payment.method"
        :paymentLink="paymentLink"
        :paymentLinkStatus="paymentLinkStatus"
        classes="text-right f-12"
      />
    </div>

    <p v-if="loading" class="f-12 mt-2 mb-0">Loading payments...</p>

    <p v-else-if="!payment" class="f-12 mt-2 mb-0">
      No payment information available.
    </p>

    <template v-else>
      <table cellspacing="0" cellpadding="1" class="table-prices">
        <tbody>
          <tr>
            <td align="left"><span class="price">Total</span></td>
            <td align="right">
              <ServicePrice
                :price="payment.totalAmount"
                :currencyCode="payment.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="price"
              />
            </td>
          </tr>
          <tr>
            <td align="left"><span class="price">Paid</span></td>
            <td align="right">
              <ServicePrice
                :price="payment.paidAmount"
                :currencyCode="payment.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="price"
              />
            </td>
          </tr>
          <tr v-if="payment.refundedAmount > 0">
            <td align="left"><span class="price">Refunded</span></td>
            <td align="right">
              <ServicePrice
                :price="payment.refundedAmount"
                :currencyCode="payment.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="price"
              />
            </td>
          </tr>
          <tr class="footer-table">
            <td align="left"><span class="totals">Pending</span></td>
            <td align="right">
              <ServicePrice
                :price="payment.pendingAmount"
                :currencyCode="payment.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="totals"
              />
            </td>
          </tr>
          <tr v-if="pendingRefundAmount > 0">
            <td align="left">
              <span class="totals text-danger">Pending refund</span>
            </td>
            <td align="right">
              <ServicePrice
                :price="pendingRefundAmount"
                :currencyCode="payment.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="totals text-danger"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <h2 class="subtitle f-16 mt-4 mb-0">Transactions</h2>

      <p v-if="!transactions.length" class="f-12 mt-2 mb-0">
        No payments have been made yet.
      </p>

      <table v-else cellspacing="0" cellpadding="1" class="table-prices">
        <tbody>
          <tr v-for="transaction in transactions" :key="transaction.id">
            <td align="left" valign="top">
              <span class="description">
                {{ transactionTypeLabels[transaction.type] || transaction.type }}
                <span v-if="transaction.method" class="font-weight-normal">
                  · {{ getMethodLabel(transaction) }}
                </span>
              </span>
              <span class="price f-12">
                {{ formatDate(transaction.created_at) }}
              </span>
              <span
                class="price f-12"
                v-if="transaction.stripe && transaction.stripe.paymentIntentId"
              >
                {{ transaction.stripe.paymentIntentId }}
                <el-tooltip content="Copy" placement="bottom">
                  <span
                    class="curp ml-1"
                    v-clipboard:copy="transaction.stripe.paymentIntentId"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onErrorCopy"
                  >
                    <i class="flaticon-copy link ml-0"></i>
                  </span>
                </el-tooltip>
              </span>
              <span class="price f-12" v-if="isCheckoutStale(transaction)">
                Amount no longer matches pending balance
              </span>
              <span class="price f-12 text-danger" v-if="transaction.failureReason">
                {{ transaction.failureReason }}
              </span>
            </td>
            <td align="right" valign="top" class="text-nowrap">
              <span v-if="transaction.type === 'REFUND'" class="totals d-inline"
                >-</span
              >
              <ServicePrice
                :price="transaction.amount"
                :currencyCode="transaction.currency"
                currencySymbol=""
                :currency="currency"
                nameClass="totals d-inline"
              />
              <span
                class="d-block f-12"
                :class="getTransactionStatusClass(transaction)"
              >
                {{ getTransactionStatusLabel(transaction) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters, mapMutations } from "vuex";
import ServicePrice from "@/components/ServicePrice.vue";
import PaymentStatus from "./PaymentStatus.vue";
import { PaymentsAPI } from "@/core/api";
import { isUnauthorizedError } from "@/helpers/commons.helpers";

const TRANSACTION_TYPE_LABELS = {
  PAYMENT: "Payment",
  REFUND: "Refund",
  CREDIT_ADJUSTMENT: "Credit Adjustment"
};

const TRANSACTION_STATUS_LABELS = {
  PENDING: "Pending",
  SUCCEEDED: "Succeeded",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired"
};

const CARD_BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  cartes_bancaires: "Cartes Bancaires"
};

const WALLET_LABELS = {
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  link: "Link"
};

const METHOD_LABELS = {
  STRIPE: "Credit/Debit Card",
  CREDIT: "Credit",
  MANUAL: "Manual",
  PAYPAL: "PayPal"
};

export default {
  name: "ReservationPaymentSummary",
  components: { ServicePrice, PaymentStatus },
  props: {
    reservationId: {
      type: String,
      default: null,
      description: "Reservation id whose payments are displayed"
    },
    paymentLink: {
      type: String,
      default: "pending",
      description: "Payment link of the reservation"
    },
    paymentLinkStatus: {
      type: String,
      default: "pending",
      description: "Payment link status of the reservation"
    }
  },
  data() {
    return {
      payment: null,
      loading: false,
      transactionTypeLabels: TRANSACTION_TYPE_LABELS,
      transactionStatusLabels: TRANSACTION_STATUS_LABELS,
      methodLabels: METHOD_LABELS
    };
  },
  computed: {
    ...mapGetters(["sesion"]),
    // No display-currency selector here: without `rates`, ServicePrice shows each amount in its own currency.
    currency() {
      return { code: "", symbol: "", rates: null };
    },
    transactions() {
      return (this.payment && this.payment.transactions) || [];
    },
    // Net paid above the current total: the reservation's price dropped after payment and the difference still has to be refunded.
    pendingRefundAmount() {
      if (!this.payment) return 0;
      const { totalAmount = 0, paidAmount = 0, refundedAmount = 0 } = this.payment;
      const difference = paidAmount - refundedAmount - totalAmount;
      return difference > 0 ? Math.round(difference * 100) / 100 : 0;
    }
  },
  watch: {
    reservationId: {
      handler(reservationId) {
        if (reservationId) this.getReservationPayment(reservationId);
      },
      immediate: true
    }
  },
  methods: {
    getMethodLabel(transaction) {
      const { card, method } = transaction;
      const methodLabel = this.methodLabels[method] || method;
      if (!card || !card.last4) return methodLabel;

      const brand = CARD_BRAND_LABELS[card.brand] || "Card";
      const wallet = WALLET_LABELS[card.wallet];
      const details = [`${brand} •••• ${card.last4}`, wallet].filter(Boolean);
      return `${methodLabel} (${details.join(", ")})`;
    },
    ...mapMutations(["setSigOut"]),
    async getReservationPayment(reservationId) {
      this.loading = true;
      try {
        const { data } = await PaymentsAPI.getReservationPayment(
          reservationId,
          this.sesion.token
        );
        this.payment = data || null;
      } catch (error) {
        if (isUnauthorizedError(error)) {
          this.setSigOut(true);
        }
        this.payment = null;
      } finally {
        this.loading = false;
      }
    },
    getTransactionStatusLabel(transaction) {
      if (transaction.status === "PENDING") {
        if (this.isCheckoutStale(transaction)) return "Outdated";
        if (this.isAwaitingCapture(transaction)) {
          return "Authorized · Pending capture";
        }
        if (this.isAwaitingCheckout(transaction)) return "Awaiting payment";
      }
      return (
        this.transactionStatusLabels[transaction.status] || transaction.status
      );
    },
    isCheckoutStale(transaction) {
      return !!(transaction.stripe && transaction.stripe.checkoutStale);
    },
    isAwaitingCheckout(transaction) {
      return (
        transaction.method === "STRIPE" &&
        !(transaction.stripe && transaction.stripe.paymentIntentId)
      );
    },
    isAwaitingCapture(transaction) {
      if (transaction.captureMode !== "DEFERRED") return false;
      if (transaction.method === "CREDIT") return true;
      return (
        transaction.method === "STRIPE" &&
        !!(transaction.stripe && transaction.stripe.paymentIntentId)
      );
    },
    getTransactionStatusClass(transaction) {
      const { status } = transaction;
      if (this.isCheckoutStale(transaction)) return "text-muted";
      if (status === "SUCCEEDED") return "text-success";
      if (status === "PENDING") return "text-warning";
      return "text-danger";
    },
    formatDate(date) {
      return date ? moment(date).format("MMM DD, YYYY HH:mm") : "";
    },
    onCopy() {
      this.$notify({
        type: "success",
        title: "Success!",
        message: "Copied to the clipboard",
        icon: "flaticon-check-3"
      });
    },
    onErrorCopy() {
      this.$notify({
        type: "danger",
        title: "Error!",
        message: "Could not be copied",
        icon: "flaticon-remove"
      });
    }
  }
};
</script>
